import type { BffApiClient } from "@/app/api-client"
import { ownerCompatibleWithBlockchains } from "@/app/nft/search/filters/resolve-owner-filter"
import type { UnionAddress } from "@anaconda/core/blockchain/address"
import {
  type NftSearchItem,
  type NftSort,
  type StatusFilterOption,
  ordersListedFirst,
} from "@anaconda/core/nft/search/types"
import { isListingExpired } from "@anaconda/core/order/listing-liveness"
import type { InferRequestType } from "hono"

/**
 * Clears a listing that has ended. The BFF drops dead listings too; this
 * covers a page that stays cached past a listing's end.
 */
export function clearExpiredListing(nft: NftSearchItem): NftSearchItem {
  if (!isListingExpired(nft.bestSellOrder)) return nft

  return { ...nft, price: undefined, bestSellOrder: undefined }
}

/**
 * Moves rows without a price after the priced ones in a grid about listings
 * (`ordersListedFirst`), so a listing that expired after the page loaded drops
 * to the end. Any other grid keeps the BFF's order.
 */
export function sortNotListedLast(
  nfts: NftSearchItem[],
  sort: NftSort,
  status: StatusFilterOption,
): NftSearchItem[] {
  if (!ordersListedFirst(sort, status)) return nfts

  const listed: NftSearchItem[] = []
  const notListed: NftSearchItem[] = []

  for (const nft of nfts) {
    if (nft.price) {
      listed.push(nft)
    } else {
      notListed.push(nft)
    }
  }

  return [...listed, ...notListed]
}

export type QueryInput = InferRequestType<
  BffApiClient["api"]["nfts"]["search"]["$post"]
>["json"]

export const getNftSearchQueryParams = (
  apiClient: BffApiClient,
  queryInput: QueryInput,
) => {
  const blockchains = queryInput.filters.blockchains
  const owner = ownerCompatibleWithBlockchains(
    queryInput.filters.owner as UnionAddress | undefined,
    blockchains,
  )
  const { owner: _ignoredOwner, ...restFilters } = queryInput.filters

  const input = {
    ...queryInput,
    filters: {
      ...restFilters,
      blockchains,
      ...(owner ? { owner } : {}),
    },
  }

  return {
    queryKey: ["nft", "search", input],
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const requestBody = { ...input, continuation: pageParam }

      const response = await apiClient.api.nfts.search.$post({
        json: requestBody,
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error(
          "[NFT Search Error]",
          response.status,
          errorData,
          "\nRequest params:",
          requestBody,
        )
        throw errorData
      }

      return response.json()
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage: { continuation?: string }) => {
      return lastPage.continuation
    },
  }
}

type QueryFn = ReturnType<typeof getNftSearchQueryParams>["queryFn"]

export type QueryResponse = Awaited<ReturnType<QueryFn>>
